import { usersApiClient } from '@/lib/apiClient';
import {
  IDeleteOrganizationResponse,
  IDeleteUnAssignLicenseByOrgUuidAndUserUuidParams,
  IGetAllOrganizationsParams,
  IGetAllOrganizationsResponse,
  IGetOrganizationsLicensesParams,
  IGetOrganizationsLicensesResponse,
  IGetOrganizationsUserParams,
  IGetOrganizationsUserResponse,
  IOrganization,
  IPostCancelLicenseByLicenseUuidParams,
  IPostCreateOrganizationParams,
  IPatchUpdateOrganizationParams,
  IPostProvisionOrganizationLicensesParams,
  IPostRenewLicenseByLicenseUuidParams,
  IPostAssignLicenseByOrgUuidUserUuidAndLicenseUuidParams,
  IPostAssignLicenseByOrgUuidUserUuidAndLicenseUuidResponse,
  IInviteUserToOrganizationParams,
  IGetOrganizationByOrgUuidParams,
  IResendInvitationToUserParams,
  IPutUserTimeLimitParams,
  IDeleteUserTimeLimitParams,
} from './types';
import {
  IPostOrganizationRequestLicenseExtensionParams,
  IPostOrganizationRequestLicenseUpgradeParams,
  IPostOrganizationRequestNewLicenseParams,
} from '@/services/licenses/types';

async function getOrganizationUsers({
  orgUuid, search, cursor, fields, lifecycleStatus,
}: IGetOrganizationsUserParams): Promise<IGetOrganizationsUserResponse> {
  const params = new URLSearchParams();

  params.set('page[limit]', '10');

  if (fields?.length) {
    params.set('fields', fields.join(','));
  }

  if (search) params.set('filter[search]', search);
  if (lifecycleStatus) params.set('filter[lifecycleStatus]', lifecycleStatus);
  if (cursor) params.set('page[cursor]', cursor);

  const response = await usersApiClient.get(`/organizations/${orgUuid}/users`, { params });
  return response.data;
}

async function getAllOrganizations({
  limit = 10,
  cursor = '',
  search = '',
}: IGetAllOrganizationsParams): Promise<IGetAllOrganizationsResponse> {
  const params = new URLSearchParams();

  if (limit) params.set('page[limit]', limit.toString());
  if (cursor) params.set('page[cursor]', cursor);
  if (search) params.set('filter[search]', search);

  const response = await usersApiClient.get('/organizations', { params });
  return response.data;
}

async function getOrganizationByOrgUuid(
  { orgUuid, fields }: IGetOrganizationByOrgUuidParams,
): Promise<IOrganization> {
  const params = new URLSearchParams();
  if (fields?.length) {
    params.set('fields', fields.join(','));
  }
  const response = await usersApiClient.get(`/organizations/${orgUuid}`, { params });
  return response.data;
}

async function getOrganizationsLicenses({
  orgUuid, status, product, licenseType, limit, cursor,
}: IGetOrganizationsLicensesParams): Promise<IGetOrganizationsLicensesResponse> {
  const params = new URLSearchParams();

  if (status && status !== 'all') params.set('filter[status]', status);
  if (product && product !== 'all') params.set('filter[product]', product);
  if (licenseType && licenseType !== 'all') params.set('filter[licenseType]', licenseType);
  if (limit) params.set('page[limit]', limit.toString());
  if (cursor) params.set('page[cursor]', cursor);

  const response = await usersApiClient.get(`/organizations/${orgUuid}/licenses`, { params });
  return response.data;
}

async function postOrganizationRequestNewLicenseMutationFn({
  orgUuid,
  licenseType,
  count,
  expiresAt,
  action,
  product,
}: IPostOrganizationRequestNewLicenseParams): Promise<void> {
  const response = await usersApiClient.post(`/organizations/${orgUuid}/license-requests`, {
    action,
    licenseType,
    count,
    expiresAt,
    product,
  });

  return response.data;
}

async function postOrganizationRequestLicenseExtensionMutationFn({
  orgUuid,
  action,
  licenseUuids,
  expiresAt,
}: IPostOrganizationRequestLicenseExtensionParams): Promise<void> {
  const response = await usersApiClient.post(`/organizations/${orgUuid}/license-requests`, {
    action,
    licenseUuids,
    expiresAt,
  });

  return response.data;
}

async function postOrganizationRequestLicenseUpgradeMutationFn({
  orgUuid,
  action,
  licenseUuids,
  targetLicenseType,
}: IPostOrganizationRequestLicenseUpgradeParams): Promise<void> {
  const response = await usersApiClient.post(`/organizations/${orgUuid}/license-requests`, {
    action,
    licenseUuids,
    targetLicenseType,
  });

  return response.data;
}

async function patchUpdateOrganizationMutationFn({
  orgUuid,
  name,
  billingAddress,
}: IPatchUpdateOrganizationParams): Promise<void> {
  const response = await usersApiClient.patch(`/organizations/${orgUuid}`, {
    name,
    ...(billingAddress !== undefined ? { billingAddress } : {}),
  });

  return response.data;
}

async function postCreateOrganizationMutationFn({
  name,
  description,
  domain,
  ownerEmail,
  billingAddress,
  settings,
  metadata,
  isPersonalOrg,
}: IPostCreateOrganizationParams): Promise<void> {
  const response = await usersApiClient.post('/organizations', {
    name,
    description,
    domain,
    ownerEmail: ownerEmail ?? '',
    billingAddress,
    settings,
    metadata,
    isPersonalOrg,
  });

  return response.data;
}

async function postProvisionOrganizationLicensesMutationFn({
  orgUuid,
  licenseType,
  count,
  expiresAt,
  product,
}: IPostProvisionOrganizationLicensesParams): Promise<void> {
  const response = await usersApiClient.post(`/organizations/${orgUuid}/licenses`, {
    product,
    licenseType,
    count,
    expiresAt,
  });

  return response.data;
}

async function postRenewLicenseByLicenseUuidMutationFn({
  licenseUuid,
  expiresAt,
}: IPostRenewLicenseByLicenseUuidParams): Promise<void> {
  const response = await usersApiClient.patch(`/licenses/${licenseUuid}`, {
    action: 'RENEW',
    expiresAt,
  });
  return response.data;
}

async function postCancelLicenseByLicenseUuidMutationFn({
  licenseUuid,
}: IPostCancelLicenseByLicenseUuidParams): Promise<void> {
  const response = await usersApiClient.patch(`/licenses/${licenseUuid}`, {
    action: 'CANCEL',
  });
  return response.data;
}

async function deleteUnAssignLicenseByOrgUuidAndUserUuidMutationFn({
  orgUuid,
  userUuid,
}: IDeleteUnAssignLicenseByOrgUuidAndUserUuidParams): Promise<void> {
  const response = await usersApiClient.delete(`/organizations/${orgUuid}/users/${userUuid}/license`);
  return response.data;
}

async function putUserTimeLimitMutationFn({
  orgUuid,
  userUuid,
  timeLimit,
}: IPutUserTimeLimitParams): Promise<void> {
  const response = await usersApiClient.put(
    `/organizations/${orgUuid}/users/${userUuid}/time-limit`,
    timeLimit,
  );
  return response.data;
}

async function deleteOrganizationByOrgUuid(
  orgUuid: IOrganization['orgUuid'],
): Promise<IDeleteOrganizationResponse> {
  const response = await usersApiClient.delete(`/organizations/${orgUuid}`);
  return response.data;
}

async function deleteUserTimeLimitMutationFn({
  orgUuid,
  userUuid,
}: IDeleteUserTimeLimitParams): Promise<void> {
  const response = await usersApiClient.delete(
    `/organizations/${orgUuid}/users/${userUuid}/time-limit`,
  );
  return response.data;
}

async function postInviteUserToOrganizationMutationFn({
  orgUuid,
  email,
  role,
}: IInviteUserToOrganizationParams): Promise<void> {
  const response = await usersApiClient.post(`/organizations/${orgUuid}/invitations`, {
    email,
    role,
  });
  return response.data;
}

async function postResendInvitationToUserMutationFn({
  orgUuid,
  email,
}: IResendInvitationToUserParams): Promise<void> {
  const response = await usersApiClient.post(`/organizations/${orgUuid}/invitations/resend`, {
    email,
  });
  return response.data;
}

async function postAssignLicenseByOrgUuidUserUuidAndLicenseUuidMutationFn({
  orgUuid,
  userUuid,
  licenseUuid,
}: IPostAssignLicenseByOrgUuidUserUuidAndLicenseUuidParams)
  : Promise<IPostAssignLicenseByOrgUuidUserUuidAndLicenseUuidResponse> {
  const response = await usersApiClient.post(
    `/organizations/${orgUuid}/users/${userUuid}/license/${licenseUuid}`,
  );

  return response.data;
}

export {
  getOrganizationUsers,
  getAllOrganizations,
  getOrganizationByOrgUuid,
  getOrganizationsLicenses,
  postOrganizationRequestNewLicenseMutationFn,
  postOrganizationRequestLicenseExtensionMutationFn,
  postOrganizationRequestLicenseUpgradeMutationFn,
  patchUpdateOrganizationMutationFn,
  postCreateOrganizationMutationFn,
  postProvisionOrganizationLicensesMutationFn,
  postRenewLicenseByLicenseUuidMutationFn,
  postCancelLicenseByLicenseUuidMutationFn,
  postAssignLicenseByOrgUuidUserUuidAndLicenseUuidMutationFn,
  deleteUnAssignLicenseByOrgUuidAndUserUuidMutationFn,
  putUserTimeLimitMutationFn,
  deleteOrganizationByOrgUuid,
  deleteUserTimeLimitMutationFn,
  postInviteUserToOrganizationMutationFn,
  postResendInvitationToUserMutationFn,
};
